import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import { logJourneyBotQuestion } from '../../../../hooks/api/psychologist/journeyTracking';
import { answerFromKnowledge } from './journeyBotKnowledge';
import type { JourneyStage } from '../../../../types/journey';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  text: string;
}

// Sugerencias contextuales por etapa — spec journey-spec-v2.md, Parte 3.10.
// 'pd' se usa cuando la psicóloga está en pending discharge (no hay número de etapa para eso).
const SUGGESTIONS_BY_STAGE: Record<number | 'pd', string[]> = {
  0: ['¿Cuándo voy a recibir mi primer paciente?', '¿Cómo configuro mis horarios?', '¿Qué es un proceso activo?'],
  1: ['¿Qué pasa si no agendo después de una sesión?', '¿Cómo reprograma mi paciente?', '¿Puedo comunicarme con mi paciente por WhatsApp?'],
  2: ['¿Cómo mejoro mi tasa de segunda sesión?', '¿Cuándo puedo derivar a un paciente?', '¿Cómo pido un bloqueo de agenda?'],
  3: ['¿Cuándo cobro mis sesiones?', '¿Qué pasa si hago menos de 15 sesiones en un mes?', '¿Cómo funciona la sesión gratuita?'],
  4: ['¿Cómo subo de grupo en PSI+?', '¿Cuándo se recalcula mi grupo?', '¿Qué beneficios tiene el grupo Avanzado?'],
  pd: ['¿Por qué no recibo nuevos pacientes?', '¿Qué pasa con los procesos que ya tengo?', '¿Qué tengo que hacer con mi agenda?'],
};

const GENERIC_FALLBACK =
  'No estoy segura de entender bien la pregunta. Podés reformularla, o si preferís, escribinos directamente a psicos@psimammoliti.com.';

interface Props {
  stage: JourneyStage;
  psychologistId: string;
  isPendingDischarge?: boolean;
}

export const JourneyBot: React.FC<Props> = ({ stage, psychologistId, isPendingDischarge }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);

  const suggestionKey: number | 'pd' = isPendingDischarge ? 'pd' : stage;
  const suggestions = SUGGESTIONS_BY_STAGE[suggestionKey] ?? SUGGESTIONS_BY_STAGE[0];

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => inputRef.current?.focus(), 100);
      const welcome: Message = {
        id: nextId.current++,
        role: 'assistant',
        text: '¡Hola! Soy tu asistente durante tu período inicial. Puedo ayudarte con dudas sobre el proceso, las políticas o tu agenda. ¿Qué necesitás saber?',
      };
      setMessages([welcome]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;

    const userMsg: Message = { id: nextId.current++, role: 'user', text: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // 1. Intenta el backend real (todavía no existe, pero queda listo para cuando lo construyan).
    // 2. Si falla, responde con la base de conocimiento local (journeyBotKnowledge.ts) — mismo
    //    patrón que PsiPlusChatbot, funciona hoy sin depender de ningún backend.
    // 3. Si tampoco hay match, un fallback genérico que deriva a psicos@.
    let answer: string | null = null;
    try {
      const response = await apiCoreAdapter.askJourneyBot({ question: trimmed, stage });
      answer = response.answer || null;
    } catch {
      // El endpoint del bot todavía no existe — se sigue abajo con la base de conocimiento local.
    }
    if (!answer) {
      answer = answerFromKnowledge(trimmed, { stage, isPendingDischarge: !!isPendingDischarge });
    }
    if (!answer) {
      answer = GENERIC_FALLBACK;
    }
    setMessages(prev => [...prev, { id: nextId.current++, role: 'assistant', text: answer as string }]);
    setIsTyping(false);
    void logJourneyBotQuestion(psychologistId, stage, trimmed, answer);
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage(input); }
  }

  const showSuggestions = messages.length <= 1 && !isTyping;

  return ReactDOM.createPortal(
    <div className="fixed bottom-6 right-6 z-[8000] flex flex-col items-end gap-3">
      {isOpen && (
        <div className="rounded-2xl border border-violet-200 bg-white shadow-2xl flex flex-col overflow-hidden w-[320px]" style={{ height: 440 }}>
          <div className="flex items-center justify-between px-4 py-3 bg-[#2D2049] text-white shrink-0">
            <div className="flex items-center gap-2">
              <Bot size={16} />
              <span className="text-sm font-semibold">Asistente Psi</span>
            </div>
            <button type="button" onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 bg-[#F8F6FF]">
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                    msg.role === 'user'
                      ? 'bg-[#2D2049] text-white rounded-br-sm'
                      : 'bg-white border border-violet-100 text-gray-700 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-violet-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1 items-center">
                  {[0, 1, 2].map(i => (
                    <span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-violet-400 inline-block animate-pulse"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {showSuggestions && (
              <div className="flex flex-col gap-2 pt-1">
                <p className="text-xs text-gray-400 font-medium">Preguntas frecuentes</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map(q => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => void sendMessage(q)}
                      className="text-xs bg-white border border-violet-200 text-violet-700 px-3 py-1.5 rounded-full hover:bg-violet-50 hover:border-violet-400 transition-colors text-left"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="px-3 py-3 bg-white border-t border-violet-100 shrink-0 flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Escribí tu consulta..."
              className="flex-1 text-sm bg-[#F8F6FF] border border-violet-100 rounded-xl px-3.5 py-2 outline-none focus:ring-1 focus:ring-violet-300 placeholder-gray-400"
            />
            <button
              type="button"
              onClick={() => void sendMessage(input)}
              disabled={!input.trim() || isTyping}
              className="bg-[#2D2049] hover:bg-[#4A3D73] disabled:opacity-40 text-white p-2 rounded-xl transition-colors"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className="flex items-center gap-2 bg-[#2D2049] hover:bg-[#4A3D73] text-white text-sm font-semibold px-4 py-3 rounded-2xl shadow-lg transition-all duration-200"
      >
        {isOpen ? <X size={16} /> : <MessageCircle size={16} />}
        {isOpen ? 'Cerrar' : 'Asistente'}
      </button>
    </div>,
    document.body,
  );
};

export default JourneyBot;
